import { useState, useEffect, useCallback } from 'react';
import api from '../api';

/**
 * Custom hook for managing the Notification Centre
 * Provides methods to fetch, mark as read, and manage notification state
 */
export default function useNotificationCentre() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
  });

  /**
   * Fetch notifications from the API
   * @param {Object} options - Query options
   * @param {number} options.limit - Number of notifications to fetch
   * @param {number} options.offset - Offset for pagination
   * @param {boolean} options.unread_only - Fetch only unread notifications
   */
  const fetchNotifications = useCallback(async (options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        limit: options.limit || 20,
        offset: options.offset || 0,
        unread_only: options.unread_only ? 'true' : 'false',
      });

      const response = await api.get(`/api/notifications?${params}`);

      if (response.data?.success) {
        setNotifications(response.data.notifications || []);
        setUnreadCount(response.data.unread_count || 0);
        setPagination({
          total: response.data.total || 0,
          limit: response.data.limit || 20,
          offset: response.data.offset || 0,
        });
      } else {
        throw new Error(response.data?.error || 'Failed to fetch notifications');
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.response?.data?.error || err.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch only the unread count (lightweight)
   */
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await api.get('/api/notifications/unread-count');

      if (response.data?.success) {
        setUnreadCount(response.data.unread_count || 0);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, []);

  /**
   * Mark specific notifications as read
   * @param {Array<number>} messageIds - Array of message IDs to mark as read
   */
  const markAsRead = useCallback(async (messageIds) => {
    if (!messageIds || messageIds.length === 0) return;

    try {
      const response = await api.post('/api/notifications/mark-read', {
        message_ids: messageIds,
      });

      if (response.data?.success) {
        // Update local state - mark notifications as read
        setNotifications(prev =>
          prev.map(notif =>
            messageIds.includes(notif.id)
              ? { ...notif, is_read: true }
              : notif
          )
        );

        // Update unread count
        setUnreadCount(prev => Math.max(0, prev - messageIds.length));

        return true;
      }
    } catch (err) {
      console.error('Error marking notifications as read:', err);
      return false;
    }
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await api.post('/api/notifications/mark-all-read');

      if (response.data?.success) {
        // Update local state - mark all as read
        setNotifications(prev =>
          prev.map(notif => ({ ...notif, is_read: true }))
        );

        // Reset unread count
        setUnreadCount(0);

        return true;
      }
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      return false;
    }
  }, []);

  /**
   * Refresh notifications (fetch latest)
   */
  const refresh = useCallback(() => {
    fetchNotifications({ limit: pagination.limit, offset: 0 });
  }, [fetchNotifications, pagination.limit]);

  /**
   * Load more notifications (pagination)
   */
  const loadMore = useCallback(() => {
    const newOffset = pagination.offset + pagination.limit;
    if (newOffset < pagination.total) {
      fetchNotifications({ limit: pagination.limit, offset: newOffset });
    }
  }, [fetchNotifications, pagination]);

  // Auto-fetch unread count on mount
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Poll for unread count every 30 seconds, skipping fetches while the tab is
  // hidden, and refresh immediately when the tab becomes visible again.
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      fetchUnreadCount();
    }, 30000); // 30 seconds

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUnreadCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    pagination,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    refresh,
    loadMore,
    hasMore: pagination.offset + notifications.length < pagination.total,
  };
}
